import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    padding: 30,
    fontSize: 9,
    fontFamily: 'Helvetica',
  },
  header: {
    textAlign: 'center',
    marginBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: '#2196F3',
    paddingBottom: 10,
  },
  statementTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#212121',
    marginTop: 10,
  },
  statementPeriod: {
    fontSize: 12,
    color: '#616161',
  },
  infoCardsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  infoCard: {
    width: '48%',
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
    padding: 8,
    backgroundColor: '#f5f5f5',
  },
  cardTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 5,
    paddingVertical: 3,
    borderRadius: 2,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
    paddingVertical: 2,
  },
  cardLabel: {
    fontSize: 8,
    color: '#424242',
    fontWeight: 'medium',
  },
  cardValue: {
    fontSize: 8,
    color: '#212121',
    fontWeight: 'bold',
  },
  cardAmountValue: {
    fontSize: 8,
    color: '#01579b',
    fontWeight: 'bold',
  },
  tableContainer: {
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#eeeeee',
  },
  tableCellHeader: {
    padding: 4,
    fontWeight: 'bold',
    fontSize: 7,
    flex: 1,
    textAlign: 'left',
  },
  tableCell: {
    padding: 4,
    fontSize: 7,
    flex: 1,
    textAlign: 'left',
  },
  tableCellRight: {
    textAlign: 'right',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: '#bdbdbd',
    paddingTop: 8,
    marginTop: 10,
  },
  footerText: {
    fontSize: 7,
    color: '#616161',
  },
  disclaimer: {
    marginTop: 8,
    padding: 6,
    backgroundColor: '#f9fafb',
    borderLeftWidth: 4,
    borderLeftColor: '#2196F3',
    borderRadius: 4,
  },
  disclaimerText: {
    fontSize: 7,
    color: '#616161',
  },
});

interface Transaction {
  txnDate: string;
  voucher: string;
  narration: string;
  debit: number;
  credit: number;
  balance: number;
}

interface ROIHistory {
  sno: number;
  startDate: string;
  endDate: string;
  roi: string;
  emiAmount: number;
}

interface StatementPdfProps {
  statement: {
    id: number;
    accountNo: string;
    loanAccountNo?: string;
    productName?: string;
    shortProductName?: string;
    period: string;
    startDate: string;
    endDate: string;
    originalLoan?: number;
    loanBalance?: number;
    amountPaid?: number;
    loanStatus?: string;
    generatedDate: string;
    transactions?: Transaction[];
    roiHistory?: ROIHistory[];
  };
}

const StatementPdf: React.FC<StatementPdfProps> = ({ statement }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginBottom: 8 }}>
            <Image
              src="/logo.png"
              style={{
                height: 60,
                marginBottom: 4,
              }}
            />
          </View>
          <Text style={styles.statementTitle}>LOAN STATEMENT</Text>
          <Text style={styles.statementPeriod}>
            Period: {statement.period}
          </Text>
        </View>

        {/* Info Cards */}
        <View style={styles.infoCardsContainer}>
          {/* Loan Information */}
          <View style={[styles.infoCard, { backgroundColor: '#e0f2f7' }]}>
            <Text style={[styles.cardTitle, { backgroundColor: '#b3e5fc' }]}>LOAN INFORMATION</Text>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Loan ID</Text>
              <Text style={styles.cardValue}>{statement.id}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Loan Account</Text>
              <Text style={styles.cardValue}>{statement.loanAccountNo || statement.accountNo}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Product</Text>
              <Text style={styles.cardValue}>{statement.productName || 'N/A'}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Status</Text>
              <Text style={styles.cardValue}>{statement.loanStatus || 'Unknown'}</Text>
            </View>
          </View>

          {/* Financial Summary */}
          <View style={[styles.infoCard, { backgroundColor: '#e8f5e9' }]}>
            <Text style={[styles.cardTitle, { backgroundColor: '#c8e6c9' }]}>FINANCIAL SUMMARY</Text>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Original Loan</Text>
              <Text style={styles.cardAmountValue}>{formatCurrency(statement.originalLoan || 0)}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Current Balance</Text>
              <Text style={styles.cardAmountValue}>{formatCurrency(statement.loanBalance || 0)}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Amount Paid</Text>
              <Text style={styles.cardAmountValue}>{formatCurrency(statement.amountPaid || 0)}</Text>
            </View>
            <View style={styles.cardRow}>
              <Text style={styles.cardLabel}>Generated Date</Text>
              <Text style={styles.cardValue}>{statement.generatedDate}</Text>
            </View>
          </View>
        </View>

        {/* Transaction History */}
        {statement.transactions && statement.transactions.length > 0 && (
          <View style={[styles.tableContainer, { backgroundColor: '#f9fafb' }]}>
            <Text style={[styles.cardTitle, { backgroundColor: '#f3f4f6', marginBottom: 5 }]}>TRANSACTION HISTORY</Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>Date</Text>
              <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>Reference</Text>
              <Text style={[styles.tableCellHeader, { flex: 2 }]}>Description</Text>
              <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 0.8 }]}>Debit</Text>
              <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 0.8 }]}>Credit</Text>
              <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 0.8 }]}>Balance</Text>
            </View>
            {statement.transactions.slice(0, 18).map((transaction, index) => (
              <View key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? '#fcfcfc' : '#ffffff' }]}>
                <Text style={[styles.tableCell, { flex: 0.8 }]}>{transaction.txnDate}</Text>
                <Text style={[styles.tableCell, { flex: 0.8 }]}>{transaction.voucher}</Text>
                <Text style={[styles.tableCell, { flex: 2 }]}>{transaction.narration}</Text>
                <Text style={[styles.tableCell, styles.tableCellRight, { flex: 0.8 }]}>
                  {transaction.debit > 0 ? formatCurrency(transaction.debit) : '-'}
                </Text>
                <Text style={[styles.tableCell, styles.tableCellRight, { flex: 0.8 }]}>
                  {transaction.credit > 0 ? formatCurrency(transaction.credit) : '-'}
                </Text>
                <Text style={[styles.tableCell, styles.tableCellRight, { flex: 0.8, fontWeight: 'bold' }]}>
                  {formatCurrency(transaction.balance)}
                </Text>
              </View>
            ))}
            {statement.transactions.length > 18 && (
              <Text style={{ textAlign: 'center', fontSize: 7, color: '#616161', marginTop: 5 }}>
                Showing first 18 transactions. Full list available in Excel export.
              </Text>
            )}
          </View>
        )}

        {/* ROI History */}
        {statement.roiHistory && statement.roiHistory.length > 0 && (
          <View style={[styles.tableContainer, { backgroundColor: '#faf5ff' }]}>
            <Text style={[styles.cardTitle, { backgroundColor: '#f3e5f5', marginBottom: 5 }]}>
              RATE OF INTEREST HISTORY
            </Text>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellHeader, { flex: 0.5 }]}>S.No</Text>
              <Text style={[styles.tableCellHeader, { flex: 1 }]}>Start Date</Text>
              <Text style={[styles.tableCellHeader, { flex: 1 }]}>End Date</Text>
              <Text style={[styles.tableCellHeader, { flex: 0.8 }]}>ROI</Text>
              <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 1 }]}>EMI Amount</Text>
            </View>
            {statement.roiHistory.map((roi, index) => (
              <View key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? '#fcfcfc' : '#ffffff' }]}>
                <Text style={[styles.tableCell, { flex: 0.5 }]}>{roi.sno}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{roi.startDate}</Text>
                <Text style={[styles.tableCell, { flex: 1 }]}>{roi.endDate}</Text>
                <Text style={[styles.tableCell, { flex: 0.8 }]}>{roi.roi}</Text>
                <Text style={[styles.tableCell, styles.tableCellRight, { flex: 1 }]}>
                  {formatCurrency(roi.emiAmount)}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Statement Summary */}
        <View style={[styles.tableContainer, { backgroundColor: '#f9fafb' }]}>
          <Text style={[styles.cardTitle, { backgroundColor: '#f3f4f6', marginBottom: 5 }]}>STATEMENT SUMMARY</Text>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCellHeader, { flex: 1.5 }]}>Description</Text>
            <Text style={[styles.tableCellHeader, styles.tableCellRight, { flex: 1 }]}>Amount</Text>
          </View>
          <View style={[styles.tableRow, { backgroundColor: '#fcfcfc' }]}>
            <Text style={[styles.tableCell, { flex: 1.5 }]}>Original Loan Amount</Text>
            <Text style={[styles.tableCell, styles.tableCellRight, { flex: 1 }]}>
              {formatCurrency(statement.originalLoan || 0)}
            </Text>
          </View>
          <View style={[styles.tableRow, { backgroundColor: '#ffffff' }]}>
            <Text style={[styles.tableCell, { flex: 1.5 }]}>Amount Paid</Text>
            <Text style={[styles.tableCell, styles.tableCellRight, { flex: 1 }]}>
              {formatCurrency(statement.amountPaid || 0)}
            </Text>
          </View>
          <View style={[styles.tableRow, { backgroundColor: '#fcfcfc' }]}>
            <Text style={[styles.tableCell, { flex: 1.5 }]}>Current Balance</Text>
            <Text style={[styles.tableCell, styles.tableCellRight, { flex: 1 }]}>
              {formatCurrency(statement.loanBalance || 0)}
            </Text>
          </View>
          <View style={[styles.tableRow, { backgroundColor: '#ffffff' }]}>
            <Text style={[styles.tableCell, { flex: 1.5 }]}>Statement Period</Text>
            <Text style={[styles.tableCell, styles.tableCellRight, { flex: 1 }]}>
              {statement.period}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          <View>
            <Text style={styles.footerText}>
              Generated By: SMEDCO System
            </Text>
            <Text style={styles.footerText}>Date & Time: {new Date().toLocaleString()}</Text>
          </View>
          <View style={{ textAlign: 'right' }}>
            <Text style={styles.footerText}>
              Page 1 of 1
            </Text>
            <Text style={{ fontSize: 10, fontWeight: 'bold', color: '#212121', marginTop: 4 }}>
              *** END OF STATEMENT ***
            </Text>
          </View>
        </View>

        {/* Disclaimer */}
        <View style={styles.disclaimer} fixed>
          <Text style={styles.disclaimerText}>
            Note: This is a computer-generated statement. For discrepancies, contact customer service within 30 days.
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default StatementPdf;
